// Re-exportar tipos del databaseService
import { databaseService, OrganizationData } from './databaseService';

export type { OrganizationData as Organization } from './databaseService';

export interface CreateOrganizationData {
  displayName: string;
  tenantKey: string;
  domains: string[];
  branding: {
    logoUrl: string;
    logoBase64?: string; // Logo almacenado como base64 para exportación PDF
    faviconUrl: string;
    colors: {
      primary: string;
      secondary: string;
      accent?: string;
    };
    logoSize?: {
      coverWidth: number;
      headerWidth: number;
    };
  };
  settings: {
    maxUsers: number;
    maxStudents: number;
    features: string[];
  };
  status: 'active' | 'trial';
  adminUser: {
    username: string;
    email: string;
    firstName: string;
    lastName: string;
    password: string;
  };
}

export interface DashboardStats {
  totalOrganizations: number;
  activeOrganizations: number;
  totalUsers: number;
  totalStudents: number;
}

class OrganizationService {
  private get API_BASE_URL() {
    return process.env.REACT_APP_API_URL || 
      (process.env.NODE_ENV === 'production' ? '' : 'http://localhost:5000');
  }

  // Sin credenciales en el navegador: si no hay token válido, el administrador debe volver a iniciar sesión.
  private async refreshTokenIfNeeded(): Promise<string | null> {
    return null;
  }

  // Helper para hacer requests con retry automático de token
  private async makeAuthenticatedRequest(url: string, options: any, maxRetries = 1): Promise<Response> {
    let token = localStorage.getItem('token');
    
    for (let attempt = 0; attempt <= maxRetries; attempt++) {
      if (!token) {
        token = await this.refreshTokenIfNeeded();
        if (!token) throw new Error('No se pudo obtener token válido');
      }

      const response = await fetch(url, {
        ...options,
        headers: {
          ...options.headers,
          'Authorization': `Bearer ${token}`
        }
      });

      // Si es 403 (token expirado) y no es el último intento, refrescar token
      if (response.status === 403 && attempt < maxRetries) {
        console.log('🔄 Token expirado, refrescando...');
        token = await this.refreshTokenIfNeeded();
        continue;
      }

      return response;
    }

    throw new Error('No se pudo completar la request después de reintentos');
  }

  async getOrganizations(): Promise<{ success: boolean; organizations: OrganizationData[]; message?: string }> {
    try {
      const API_BASE_URL = process.env.REACT_APP_API_URL || 
        (process.env.NODE_ENV === 'production' ? '' : 'http://localhost:5000');

      const token = localStorage.getItem('token');
      if (!token) {
        throw new Error('No hay token de autenticación');
      }

      const response = await fetch(`${API_BASE_URL}/api/organizations`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error(`Error HTTP: ${response.status}`);
      }

      const data = await response.json();
      
      if (!data.success) {
        throw new Error(data.message || 'Error obteniendo organizaciones');
      }

      return {
        success: true,
        organizations: data.organizations
      };
    } catch (error) {
      return {
        success: false,
        organizations: [],
        message: 'Error al cargar organizaciones'
      };
    }
  }

  async getOrganization(tenantKey: string): Promise<{ success: boolean; organization?: OrganizationData; message?: string }> {
    try {
      const API_BASE_URL = process.env.REACT_APP_API_URL || 
        (process.env.NODE_ENV === 'production' ? '' : 'http://localhost:5000');

      const token = localStorage.getItem('token');
      if (!token) {
        throw new Error('No hay token de autenticación');
      }

      const response = await fetch(`${API_BASE_URL}/api/organizations/${tenantKey}`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        if (response.status === 404) {
          return {
            success: false,
            message: 'Organización no encontrada'
          };
        }
        throw new Error(`Error HTTP: ${response.status}`);
      }

      const data = await response.json();
      
      if (!data.success) {
        throw new Error(data.message || 'Error obteniendo organización');
      }

      return {
        success: true,
        organization: data.organization
      };
    } catch (error) {
      return {
        success: false,
        message: error instanceof Error ? error.message : 'Error al cargar organización'
      };
    }
  }

  async createOrganization(data: CreateOrganizationData): Promise<{ success: boolean; organization?: OrganizationData; message?: string }> {
    try {
      const API_BASE_URL = process.env.REACT_APP_API_URL || 
        (process.env.NODE_ENV === 'production' ? '' : 'http://localhost:5000');

      // Token de la sesión del administrador del sistema (sin credenciales en el navegador)
      const systemAdminToken = localStorage.getItem('token');
      if (!systemAdminToken) {
        throw new Error('Sesión expirada: inicie sesión nuevamente como administrador del sistema');
      }

      const response = await fetch(`${API_BASE_URL}/api/organizations`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${systemAdminToken}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(data)
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || `Error HTTP: ${response.status}`);
      }

      const result = await response.json();
      
      if (!result.success) {
        throw new Error(result.message || 'Error creando organización');
      }

      return {
        success: true,
        organization: result.organization
      };
    } catch (error) {
      console.error('❌ Error en API para crear organización:', error);
      
      return {
        success: false,
        message: error instanceof Error ? error.message : 'Error al crear organización'
      };
    }
  }

  async updateOrganization(tenantKey: string, updates: Partial<OrganizationData>): Promise<{ success: boolean; organization?: OrganizationData; message?: string }> {
    try {
      const updatedOrganization = await databaseService.updateOrganization(tenantKey, updates);
      
      if (!updatedOrganization) {
        return {
          success: false,
          message: 'Organización no encontrada'
        };
      }

      return {
        success: true,
        organization: updatedOrganization
      };
    } catch (error) {
      return {
        success: false,
        message: 'Error al actualizar organización'
      };
    }
  }

  async updateOrganizationStatus(tenantKey: string, newStatus: 'trial' | 'active' | 'suspended' | 'inactive'): Promise<{ success: boolean; message?: string }> {
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        throw new Error('No hay token de autenticación');
      }

      console.log(`🔄 Actualizando status de organización ${tenantKey} a: ${newStatus}`);

      const response = await fetch(`${this.API_BASE_URL}/api/organizations/${tenantKey}/status`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ status: newStatus })
      });

      if (response.ok) {
        const result = await response.json();
        console.log(`✅ Status de organización actualizado exitosamente: ${tenantKey}`);
        return {
          success: true,
          message: `Organización ${newStatus === 'active' ? 'activada' : 'actualizada'} exitosamente`
        };
      } else {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Error al actualizar status');
      }
    } catch (error) {
      console.error('Error actualizando status de organización:', error);
      return {
        success: false,
        message: error instanceof Error ? error.message : 'Error al actualizar status de organización'
      };
    }
  }

  async deleteOrganization(tenantKey: string): Promise<{ success: boolean; message?: string }> {
    try {
      const API_BASE_URL = process.env.REACT_APP_API_URL || 
        (process.env.NODE_ENV === 'production' ? '' : 'http://localhost:5000');

      const token = localStorage.getItem('token');
      if (!token) {
        throw new Error('No hay token de autenticación');
      }

      console.log(`🗑️ Eliminando organización via API: ${tenantKey}`);

      const response = await fetch(`${API_BASE_URL}/api/organizations/${tenantKey}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        if (response.status === 404) {
          return {
            success: false,
            message: 'Organización no encontrada'
          };
        }
        const errorData = await response.json();
        throw new Error(errorData.message || `Error HTTP: ${response.status}`);
      }

      const data = await response.json();
      
      if (!data.success) {
        throw new Error(data.message || 'Error eliminando organización');
      }

      console.log(`✅ Organización eliminada exitosamente: ${tenantKey}`);

      return {
        success: true,
        message: data.message || 'Organización eliminada correctamente'
      };
    } catch (error) {
      console.error(`❌ Error eliminando organización ${tenantKey}:`, error);
      return {
        success: false,
        message: error instanceof Error ? error.message : 'Error al eliminar organización'
      };
    }
  }



  async getDashboardStats(): Promise<{ success: boolean; stats?: DashboardStats; message?: string }> {
    try {
      const API_BASE_URL = process.env.REACT_APP_API_URL || 
        (process.env.NODE_ENV === 'production' ? '' : 'http://localhost:5000');

      const token = localStorage.getItem('token');
      if (!token) {
        throw new Error('No hay token de autenticación');
      }

      const response = await fetch(`${API_BASE_URL}/api/dashboard-stats`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error(`Error HTTP: ${response.status}`);
      }

      const data = await response.json();
      
      if (!data.success) {
        throw new Error(data.message || 'Error obteniendo estadísticas');
      }

      return {
        success: true,
        stats: data.stats
      };
    } catch (error) {
      return {
        success: false,
        message: error instanceof Error ? error.message : 'Error al cargar estadísticas'
      };
    }
  }

  // Nuevo método para subir archivos
  async uploadFile(file: File, organizationId: string, type: 'logo' | 'favicon'): Promise<{ success: boolean; url?: string; message?: string }> {
    try {
      const url = await databaseService.uploadFile(file, organizationId, type);
      return {
        success: true,
        url
      };
    } catch (error) {
      return {
        success: false,
        message: 'Error al subir archivo'
      };
    }
  }

  // Métodos para crear datos adicionales de la organización
  async createTeacher(organizationId: string, teacherData: any): Promise<{ success: boolean; message?: string }> {
    try {
      // Mapear la estructura de datos correctamente para la API según estructura real MongoDB
      const apiTeacherData = {
        username: teacherData.username,
        email: teacherData.email,
        password: teacherData.password,
        firstName: teacherData.firstName,
        lastName: teacherData.lastName,
        role: teacherData.role,
        allowedCourses: teacherData.courses || [],
        allowedDimensions: teacherData.role === 'admin' ? [] : [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 12], // Sin 11, según estructura real
        isActive: teacherData.isActive !== undefined ? teacherData.isActive : true,
        mustChangePassword: teacherData.mustChangePassword !== undefined ? teacherData.mustChangePassword : true,
        phone: teacherData.phone || '',
        emergencyContact: '',
        goals: [],
        failedLoginAttempts: 0 // Campo requerido según estructura MongoDB
      };

      const response = await this.makeAuthenticatedRequest(`${this.API_BASE_URL}/api/system-admin/users`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ organizationId, ...apiTeacherData })
      });

      if (response.ok) {
        return { success: true };
      } else {
        let errorMessage = `Error HTTP: ${response.status}`;
        try {
          const errorData = await response.json();
          errorMessage = errorData.message || errorData.error || errorMessage;
        } catch (e) {
          // Si no se puede parsear el JSON, usar mensaje genérico
          errorMessage = `Error interno del servidor (${response.status})`;
        }
        throw new Error(errorMessage);
      }
    } catch (error) {
      return {
        success: false,
        message: error instanceof Error ? error.message : 'Error al crear profesor'
      };
    }
  }

  // Función para generar skillLevels por defecto - CORREGIDA para coincidir con frontend
  private generateDefaultSkillLevels(): Array<{ dimensionId: number; skillId: number; level: number }> {
    const skillLevels = [];
    const dimensionsConfig = [
      { id: 1, skills: 8 },    // Autoconciencia y Voluntad
      { id: 2, skills: 39 },   // Habilidades de Pensamiento  
      { id: 3, skills: 76 },   // Habilidades comunicacionales
      { id: 4, skills: 44 },   // Corporalidad y Movimiento
      { id: 5, skills: 46 },   // Lenguajes Artísticos
      { id: 6, skills: 7 },    // Motivación y disposición hacia el aprendizaje
      { id: 7, skills: 52 },   // Encuentro con la Naturaleza y Sociedad
      { id: 8, skills: 8 },    // Habilidades de relación y conciencia social
      { id: 9, skills: 194 },  // Pensamiento matemático ✅ CORREGIDO
      { id: 10, skills: 34 },  // Acercamiento a la lengua inglesa
      { id: 11, skills: 6 }    // Amor a Dios y sentido Cristiano
    ];

    for (const dimension of dimensionsConfig) {
      for (let skillId = 1; skillId <= dimension.skills; skillId++) {
        skillLevels.push({
          dimensionId: dimension.id,
          skillId: skillId,
          level: 1 // Nivel por defecto - MongoDB generará _id automáticamente
        });
      }
    }

    return skillLevels;
  }

  async createStudent(organizationId: string, studentData: any): Promise<{ success: boolean; message?: string }> {
    try {
      // Mapear la estructura de datos correctamente para la API
      const apiStudentData = {
        id: studentData.id, // RUT u otro identificador
        name: studentData.name,
        course: studentData.course,
        birthDate: studentData.birthDate,
        skillLevels: this.generateDefaultSkillLevels(), // Array de 514 elementos
        qualitativeDescription: "",
        createdBy: "sistema",
        updatedBy: "sistema"
      };

      const response = await this.makeAuthenticatedRequest(`${this.API_BASE_URL}/api/system-admin/students`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ organizationId, ...apiStudentData })
      });

      if (response.ok) {
        return { success: true };
      } else {
        let errorMessage = `Error HTTP: ${response.status}`;
        try {
          const errorData = await response.json();
          errorMessage = errorData.message || errorData.error || errorMessage;
        } catch (e) {
          // Si no se puede parsear el JSON, usar mensaje genérico
          errorMessage = `Error interno del servidor (${response.status})`;
        }
        throw new Error(errorMessage);
      }
    } catch (error) {
      return {
        success: false,
        message: error instanceof Error ? error.message : 'Error al crear estudiante'
      };
    }
  }

  async createCourse(organizationId: string, courseData: any): Promise<{ success: boolean; message?: string }> {
    try {
      const response = await this.makeAuthenticatedRequest(`${this.API_BASE_URL}/api/system-admin/courses`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ organizationId, ...courseData })
      });

      if (response.ok) {
        return { success: true };
      } else {
        let errorMessage = `Error HTTP: ${response.status}`;
        try {
          const errorData = await response.json();
          errorMessage = errorData.message || errorData.error || errorMessage;
        } catch (e) {
          // Si no se puede parsear el JSON, usar mensaje genérico
          errorMessage = `Error interno del servidor (${response.status})`;
        }
        throw new Error(errorMessage);
      }
    } catch (error) {
      return {
        success: false,
        message: error instanceof Error ? error.message : 'Error al crear curso'
      };
    }
  }

  // Funciones batch para mejor rendimiento
  async createTeachersBatch(organizationId: string, teachersData: any[]): Promise<{ success: boolean; created: number; failed: number; errors: string[] }> {
    const results = { success: true, created: 0, failed: 0, errors: [] as string[] };
    
    // Procesar en lotes de 5 para evitar sobrecarga
    const batchSize = 5;
    for (let i = 0; i < teachersData.length; i += batchSize) {
      const batch = teachersData.slice(i, i + batchSize);
      const promises = batch.map(teacherData => this.createTeacher(organizationId, teacherData));
      
      const batchResults = await Promise.allSettled(promises);
      
      batchResults.forEach((result, index) => {
        if (result.status === 'fulfilled' && result.value.success) {
          results.created++;
        } else {
          results.failed++;
          const teacherEmail = batch[index].email || 'desconocido';
          const error = result.status === 'rejected' ? result.reason : result.value.message;
          results.errors.push(`${teacherEmail}: ${error}`);
        }
      });

      // Pequeña pausa entre lotes para no sobrecargar el servidor
      if (i + batchSize < teachersData.length) {
        await new Promise(resolve => setTimeout(resolve, 100));
      }
    }

    results.success = results.failed === 0;
    return results;
  }

  async createCoursesBatch(organizationId: string, coursesData: any[]): Promise<{ success: boolean; created: number; failed: number; errors: string[] }> {
    const results = { success: true, created: 0, failed: 0, errors: [] as string[] };
    
    const batchSize = 10; // Los cursos son más simples, podemos hacer lotes más grandes
    for (let i = 0; i < coursesData.length; i += batchSize) {
      const batch = coursesData.slice(i, i + batchSize);
      const promises = batch.map(courseData => this.createCourse(organizationId, courseData));
      
      const batchResults = await Promise.allSettled(promises);
      
      batchResults.forEach((result, index) => {
        if (result.status === 'fulfilled' && result.value.success) {
          results.created++;
        } else {
          results.failed++;
          const courseName = batch[index].name || 'desconocido';
          const error = result.status === 'rejected' ? result.reason : result.value.message;
          results.errors.push(`${courseName}: ${error}`);
        }
      });

      if (i + batchSize < coursesData.length) {
        await new Promise(resolve => setTimeout(resolve, 50));
      }
    }

    results.success = results.failed === 0;
    return results;
  }

  async createStudentsBatch(organizationId: string, studentsData: any[]): Promise<{ success: boolean; created: number; failed: number; errors: string[] }> {
    const results = { success: true, created: 0, failed: 0, errors: [] as string[] };
    
    const batchSize = 3; // Los estudiantes tienen muchos skillLevels, lotes más pequeños
    for (let i = 0; i < studentsData.length; i += batchSize) {
      const batch = studentsData.slice(i, i + batchSize);
      const promises = batch.map(studentData => this.createStudent(organizationId, studentData));
      
      const batchResults = await Promise.allSettled(promises);
      
      batchResults.forEach((result, index) => {
        if (result.status === 'fulfilled' && result.value.success) {
          results.created++;
        } else {
          results.failed++;
          const studentName = batch[index].name || 'desconocido';
          const error = result.status === 'rejected' ? result.reason : result.value.message;
          results.errors.push(`${studentName}: ${error}`);
        }
      });

      if (i + batchSize < studentsData.length) {
        await new Promise(resolve => setTimeout(resolve, 200)); // Pausa más larga para estudiantes
      }
    }

    results.success = results.failed === 0;
    return results;
  }
}

export const organizationService = new OrganizationService();
export default organizationService;